import React, { useState, useEffect, useContext } from 'react';
import { RuntimeContext } from '../../../../common/RuntimeContext';
import PropTypes from 'prop-types';
import { Accordion, Row } from 'react-bootstrap';
import VerbParagraph from '../../../shared/components/VerbParagraph';
import Image from '../../../shared/components/Image';
import VerbButton from '../../../shared/components/VerbButton';
import VerbAccordionButton from '../../../shared/components/VerbAccordionButton';
import { VerbFetch } from '../../../shared/VerbFetch';
import { LEARNING_ASSET_DURATIONS } from '../../../shared/Constants';

const ContentCatalog = () => {
    const config = useContext(RuntimeContext)
    const [selectedContent, setSelectedContent] = useState('Collections');
    const [collectionContent, setCollectionContent] = useState([]);
    const [pathwayContent, setPathwayContent] = useState([]);
    const [collectionFilter, setCollectionFilter] = useState('');

    const durationDict = LEARNING_ASSET_DURATIONS();

    useEffect(() => {
        VerbFetch(`${config.pathsWeb.clientUrl}/contentcatalog`).then(({ pathways, collections }) => {
            setCollectionContent(collections);
            setPathwayContent(pathways);
        });
    }, []);

    const CollectionContentRow = ({ name, thumbnailImageUrl }) => (
        <div className={'collection-content-row'}>
            <div className={'collection-image'}>
                <Image src={thumbnailImageUrl} dimension={{ h: 24, w: 24 }} alt={'collection image'} />
            </div>
            <p className={'collection-name'}>{name}</p>
        </div>
    );

    CollectionContentRow.propTypes = {
        name: PropTypes.string,
        thumbnailImageUrl: PropTypes.string
    };

    const ActivityContentRow = ({ name, durationId }) => (
        <div className={'activity-content-row'}>
            <p className={'activity-name'}>{name}</p>
            <p className={'activity-duration'}>{`${durationDict[durationId]?.inMinutes} MINS`}</p>
        </div>
    );

    ActivityContentRow.propTypes = {
        name: PropTypes.string,
        durationId: PropTypes.string
    };

    const PathwayBodyHeader = () => (
        <div className={'container'}>
            <div className={'row'}>
                <div className={`col-12 pathway-body-header`}>
                    <div className={'row'}>
                        <div className={'col-12 col-md-3 offset-lg-1 d-none d-md-block herbie-img'}>
                            <Image
                                src={
                                    'https://res.cloudinary.com/verb-prod/image/upload/v1649787843/herbie_white_tx0akw.png'
                                }
                                dimension={{ h: 416, w: 416 }}
                                alt={'herbie'}
                            />
                        </div>
                        <div className={'col-md-8 col-12'}>
                            <p className={'pathway-text'}>
                                A pathway is a group of collections that make up a longer learning journey.
                            </p>
                            <p className={'pathway-text'}>
                                We have ready-to-go pathways and offers the ability for you to build your own. Use
                                pathways as the backbone of your leadership development program, combining collections
                                with events and off-platform learning.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    const filterButton = (name) => (
        <p
            className={`collection-filter ${name === collectionFilter ? 'is-selected' : ''}`}
            onClick={() => setCollectionFilter(collectionFilter && name === collectionFilter ? '' : name)}
        >
            {name}
        </p>
    );

    const dimensionMap = {
        'Know Yourself': ['5dcc3a6f69bd81000fa66fc5', '5ce5b2dbbf428bd64d8ce8bc'],
        'Foster Collaboration & Culture': ['5ce5b2dbbf428bd64d8ce8bb'],
        'Enhance Creativity & Innovation': ['5ce5b2dbbf428bd64d8ce8ba'],
        'Drive Results': ['5ce5b2dbbf428bd64d8ce8b9', '5dcc3a4169bd81000fa66fc4'],
        'Cultivate a Growth Mindset': ['5ce5b2dbbf428bd64d8ce8b8'],
        'Build High-Performing Teams': ['5ce5b2dbbf428bd64d8ce8b7']
    };

    const CollectionBodyHeader = () => (
        <div className={'container'}>
            <div className={'row'}>
                <div className={`col-12 collection-body-header`}>
                    <div className={'row'}>
                        <div className={'collection-info'}>
                            <div className={'row'}>
                                <p className={'col-12'}>
                                    A collection is a group of 3-5 bite-sized activities that focus on a specific
                                    leadership skill or capability
                                </p>
                            </div>
                        </div>
                        <div className={'col-12 grouping-one'}>
                            <div className={'row filters'}>
                                {[
                                    'Know Yourself',
                                    'Foster Collaboration & Culture',
                                    'Enhance Creativity & Innovation',
                                    'Drive Results',
                                    'Cultivate a Growth Mindset',
                                    'Build High-Performing Teams'
                                ].map(filterButton)}
                            </div>
                        </div>
                        <div className={'col-12 grouping-two'}>
                            <div className={'row filters'}>
                                {['Know Yourself', 'Foster Collaboration & Culture'].map(filterButton)}
                            </div>
                            <div className={'row filters second-row'}>
                                {['Enhance Creativity & Innovation', 'Drive Results', 'Cultivate a Growth Mindset'].map(
                                    filterButton
                                )}
                            </div>
                            <div className={'row filters'}>{['Build High-Performing Teams'].map(filterButton)}</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    const calculateMinutesForCollection = (contentArray) =>
        contentArray.reduce((acc, content) => acc + durationDict[content.durationId]?.inMinutes, 0);

    const CollectionContentCard = (contentItem) => {
        const { _id, thumbnailImageUrl, name, subtitle, contentArray = [], durationInMinutes = 0 } = contentItem;

        const [isOpen, setOpen] = useState(false);

        const toggleAccordion = () => {
            setOpen(!isOpen);
        };

        return (
            <Accordion onSelect={toggleAccordion}>
                <div className={'col-12 collection-content-card'} eventKey={'0'}>
                    <VerbAccordionButton className={'row'} eventKey={_id}>
                        <span className={`col-lg-3 col-12 collection-content-image`}>
                            <div className={'row'}>
                                <Image
                                    className={`col-12`}
                                    src={thumbnailImageUrl}
                                    dimension={{ h: 256, w: 256 }}
                                    alt={'content image'}
                                />
                            </div>
                        </span>
                        <span className={`col-lg-9 col-12 collection-info-container`}>
                            <div className={'row'}>
                                <span className={`col-lg-11 collection-info-text`}>
                                    <h4 className={'text-lg-start text-center'}>{name}</h4>
                                    <i
                                        className={`fal ${
                                            isOpen ? 'fa-angle-up' : 'fa-angle-down'
                                        } fa-3x content-toggle d-sm-none d-none d-md-block`}
                                    ></i>
                                    <p className={'collection-description'}>{subtitle}</p>
                                </span>
                                <span className={`col-12 collection-content-stats`}>
                                    <p className={'activity-number'}>{`${contentArray.length} ACTIVITIES`}</p>
                                    <p className={'collection-duration'}>{`${
                                        durationInMinutes || calculateMinutesForCollection(contentArray)
                                    } MINS`}</p>
                                </span>
                                <div className={'d-md-none d-sm-block mobile-caret col-12'}>
                                    <i
                                        className={`fal ${
                                            isOpen ? 'fa-angle-up' : 'fa-angle-down'
                                        } fa-3x content-toggle`}
                                    ></i>
                                </div>
                            </div>
                        </span>
                    </VerbAccordionButton>
                    <Accordion.Collapse as={'div'} eventKey={_id}>
                        <>{contentArray.map(ActivityContentRow)}</>
                    </Accordion.Collapse>
                </div>
            </Accordion>
        );
    };

    const PathwayContentCard = (contentItem) => {
        const { name, subtitle, contentArray = [], _id } = contentItem;

        const [isOpen, setOpen] = useState(false);

        const toggleAccordion = () => {
            setOpen(!isOpen);
        };
        return (
            <div className={`col-12 pathway-content-card`}>
                <div className={'row'}>
                    <span className={`col-12 pathway-info-container`}>
                        <Accordion onSelect={toggleAccordion}>
                            <VerbAccordionButton className={'row pathway-info-row'} eventKey={_id}>
                                <span className={`col-12`}>
                                    <div className={'row'}>
                                        <h4 className={'col-12'}>{name}</h4>
                                        <p className={'col-11'}>{subtitle}</p>
                                        <i
                                            className={`col-1 fal ${
                                                isOpen ? 'fa-angle-up' : 'fa-angle-down'
                                            } fa-3x content-toggle`}
                                        ></i>
                                    </div>
                                </span>
                                <span className={`col-12 pathway-content-stats`}>
                                    <p className={'collection-number'}>{`${contentArray.length} COLLECTIONS`}</p>
                                </span>
                            </VerbAccordionButton>
                            <Accordion.Collapse as={'div'} eventKey={_id}>
                                <>{contentArray.map(CollectionContentRow)}</>
                            </Accordion.Collapse>
                        </Accordion>
                    </span>
                </div>
            </div>
        );
    };

    const splitContentCardsIntoTwoColumns = (content) =>
        content.reduce(
            (acc, contentItem, index) => {
                if (index % 2 === 0) {
                    acc[0].push(contentItem);
                    return acc;
                }
                acc[1].push(contentItem);
                return acc;
            },
            [[], []]
        );

    const PathwayContentPage = () => {
        const [columnOne, columnTwo] = splitContentCardsIntoTwoColumns(pathwayContent);
        return (
            <div className={'container content-page-container'}>
                <div className={'row'}>
                    <div className={`col-md-6 col-12 content-page`}>
                        <div className={'row'}>
                            <div className={'col-12'}>{columnOne.map(PathwayContentCard)}</div>
                        </div>
                    </div>
                    <div className={`col-md-6 col-12 content-page`}>
                        <div className={'row'}>
                            <div className={'col-12'}>{columnTwo.map(PathwayContentCard)}</div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const filterCollections = (collections) =>
        collections.filter((collection) => dimensionMap[collectionFilter]?.includes(collection?.dimensionId));

    const CollectionContentPage = () => {
        const [columnOne, columnTwo] = splitContentCardsIntoTwoColumns(
            collectionFilter ? filterCollections(collectionContent) : collectionContent
        );

        return (
            <div className={'content-page-container container'}>
                <div className={'row'}>
                    <div className={`col-12 col-md-6 content-page`}>
                        <div className={'row'}>
                            <div className={'col-12'}>
                                {columnOne.map((collection) => (
                                    <CollectionContentCard {...collection} />
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className={`col-12 col-md-6 content-page`}>
                        <div className={'row'}>
                            <div className={'col-12'}>
                                {columnTwo.map((collection) => (
                                    <CollectionContentCard {...collection} />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className={'content-catalog'}>
            <Row
                style={{
                    backgroundColor: '#F06F1F',
                    backgroundPosition: '0px 2100px'
                }}
                className={'content-catalog-header'}
            >
                <div className={`container header-container`}>
                    <div className={'row'}>
                        <div className={'col-12 col-lg-2 offset-lg-1 content-catalog-image'}>
                            <Image
                                dimension={{ w: 144 }}
                                src={
                                    'https://res.cloudinary.com/verb-prod/image/upload/v1684912009/Constants/PaycorLogo_White_144.png'
                                }
                            />
                        </div>
                        <div className={'col-10 offset-1 offset-sm-1 col-md-12 offset-md-0 col-lg-9'}>
                            <div className={'row'}>
                                <VerbParagraph
                                    className={'col-12 collection-experience-skill-text'}
                                    defaultMessage={`Leadership & Soft Skills Learning Catalog`}
                                    messageId={'CONTENT_CATALOG'}
                                />
                                <VerbParagraph
                                    className={'col-12 collection-experience-paragraph'}
                                    defaultMessage={`With over 100 hours of interactive and original content, we help leaders develop human-centered mindsets and capabilities such as empathy, listening, goal setting, and building trust. Paycor engages people to learn new tools and strategies, gives them a safe place to practice, and prompts them to apply what they’re learning in the real world.`}
                                    messageId={'CONTENT_CATALOG_HEADER_TEXT'}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div className={'col-12 content-selector-container'}>
                    <div className={'content-selector-base row'}>
                        <div className={'col-12 content-selector'}>
                            <div className={'selector-button-container'}>
                                <VerbButton
                                    onClick={() => setSelectedContent('Collections')}
                                    className={`selector-buttons ${
                                        selectedContent === 'Collections' ? '' : 'is-not-selected'
                                    }`}
                                    buttonText={'Collections'}
                                />
                            </div>
                            <div className={'selector-button-container'}>
                                <VerbButton
                                    onClick={() => setSelectedContent('Pathways')}
                                    className={`selector-buttons ${
                                        selectedContent === 'Pathways' ? '' : 'is-not-selected'
                                    }`}
                                    buttonText={'Pathways'}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </Row>
            <div className={'catalog-body'}>
                {selectedContent === 'Collections' && (
                    <>
                        <CollectionBodyHeader />
                        <CollectionContentPage content={collectionContent} />
                    </>
                )}
                {selectedContent === 'Pathways' && (
                    <>
                        <PathwayBodyHeader />
                        <PathwayContentPage content={pathwayContent} />
                    </>
                )}
            </div>
        </div>
    );
};

ContentCatalog.propTypes = {};

export default ContentCatalog;
